import {
  Button,
  CounterClockwiseClockIcon,
  IconSize,
  ReloadIcon,
  Share1Icon,
  Tooltip,
} from '@protoss/ui';
import * as React from 'react';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { SplitPaneLayout } from '../../../components/SplitPaneLayout';
import { useLayout } from '../../../contexts/LayoutContext';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import { formatDateTimeFull } from '../../../lib/datetime';
import { useCommitHistory } from '../hooks/useCommitHistory';
import { useManualSync } from '../hooks/useManualSync';
import { useSourceSystem } from '../hooks/useSourceSystem';
import type { CommitHistoryItem } from '../types/source-system';

// ── Constants ──────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

// ── Sub-components ─────────────────────────────────────────────────────────

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <ReloadIcon
      width={IconSize.sm}
      height={IconSize.sm}
      aria-hidden="true"
      className={spinning ? 'ss-panel__action-icon--spinning' : undefined}
    />
  );
}

function CommitTypeBadge({ type }: { type: 'commit' | 'pull_request' }) {
  return (
    <span
      className={`ch-badge ch-badge--${type === 'commit' ? 'commit' : 'pr'}`}
      aria-label={type === 'commit' ? 'Commit' : 'Pull Request'}
    >
      {type === 'commit' ? 'Commit' : 'PR'}
    </span>
  );
}

function CommitStatusBadge({ status }: { status: string }) {
  return <span className={`ch-status ch-status--${status.toLowerCase()}`}>{status}</span>;
}

function HistoryTableHeader() {
  return (
    <div className="ch-table__header" aria-hidden="true">
      <div className="ch-table__col ch-table__col--type">Type</div>
      <div className="ch-table__col ch-table__col--date">Date / Time</div>
      <div className="ch-table__col ch-table__col--author">Author</div>
      <div className="ch-table__col ch-table__col--hash">Hash / #</div>
      <div className="ch-table__col ch-table__col--title">Title</div>
    </div>
  );
}

function HistoryItem({
  item,
  selected,
  onSelect,
}: {
  item: CommitHistoryItem;
  selected: boolean;
  onSelect: (uuid: string) => void;
}) {
  const hash =
    item.artifactType === 'commit'
      ? (item.shortSha ?? '-')
      : item.prNumber
        ? `#${item.prNumber}`
        : '-';

  return (
    <div
      className={`ch-table__row ch-item--clickable${selected ? ' ch-item--selected' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(item.uuid)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(item.uuid);
      }}
      aria-pressed={selected}
    >
      <div className="ch-table__col ch-table__col--type">
        <CommitTypeBadge type={item.artifactType} />
      </div>
      <div className="ch-table__col ch-table__col--date">
        {formatDateTimeFull(item.artifactCreatedAt)}
      </div>
      <div className="ch-table__col ch-table__col--author">{item.actorDisplayName ?? '-'}</div>
      <div className="ch-table__col ch-table__col--hash ch-table__col--mono">{hash}</div>
      <div className="ch-table__col ch-table__col--title">{item.title}</div>
    </div>
  );
}

function HistoryEmptyState({ hasRepos }: { hasRepos: boolean }) {
  return (
    <div className="ch-empty">
      <CounterClockwiseClockIcon
        width={IconSize.xl}
        height={IconSize.xl}
        aria-hidden="true"
        className="ch-empty__icon"
      />
      {hasRepos ? (
        <>
          <p className="ch-empty__title">No commit history yet</p>
          <p className="ch-empty__body">
            Run a sync to pull recent commits and pull requests from your connected repositories.
          </p>
        </>
      ) : (
        <>
          <p className="ch-empty__title">No repositories connected</p>
          <p className="ch-empty__body">
            Connect a repository under Repos, then sync to see commit history here.
          </p>
        </>
      )}
    </div>
  );
}

function CommitDetail({ item }: { item: CommitHistoryItem }) {
  const { copied, copy } = useCopyToClipboard();

  function handleCopy() {
    if (!item.externalUrl) return;
    void copy(item.externalUrl);
  }

  return (
    <div className="ch-detail">
      <div className="ch-detail__type-row">
        <CommitTypeBadge type={item.artifactType} />
        {item.artifactType === 'pull_request' && item.status && (
          <CommitStatusBadge status={item.status} />
        )}
        {item.externalUrl && (
          <div className="ch-detail__link-actions">
            <Tooltip.Simple label="Copy link to clipboard">
              <Button type="button" variant="ghost" size="sm" onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </Tooltip.Simple>
            <Button variant="ghost" size="sm" asChild>
              <a href={item.externalUrl} target="_blank" rel="noopener noreferrer">
                <Share1Icon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
                Open
              </a>
            </Button>
          </div>
        )}
      </div>

      <p className="ch-detail__title">{item.title}</p>

      <dl className="ch-detail__meta">
        {item.repoName && (
          <>
            <dt>Repository</dt>
            <dd>{item.repoName}</dd>
          </>
        )}
        {item.artifactType === 'commit' && item.shortSha && (
          <>
            <dt>SHA</dt>
            <dd className="ch-detail__mono">{item.shortSha}</dd>
          </>
        )}
        {item.artifactType === 'pull_request' && item.prNumber && (
          <>
            <dt>PR</dt>
            <dd className="ch-detail__mono">#{item.prNumber}</dd>
          </>
        )}
        {item.branchName && (
          <>
            <dt>Branch</dt>
            <dd className="ch-detail__mono">{item.branchName}</dd>
          </>
        )}
        {item.actorDisplayName && (
          <>
            <dt>{item.artifactType === 'commit' ? 'Author' : 'Creator'}</dt>
            <dd>
              {item.actorDisplayName}
              {item.actorEmail && (
                <span className="ch-detail__email"> &lt;{item.actorEmail}&gt;</span>
              )}
            </dd>
          </>
        )}
        {item.artifactCreatedAt && (
          <>
            <dt>Date</dt>
            <dd>{formatDateTimeFull(item.artifactCreatedAt)}</dd>
          </>
        )}
      </dl>
    </div>
  );
}
// ── Main component ──────────────────────────────────────────────────────────

interface CommitsPageProps {
  projectUuid: string;
  breadcrumb: string;
  canEdit: boolean;
  refreshKey?: number;
  onRunStateChange?: (runUuid: string | null, isActive: boolean) => void;
}

/**
 * Commits page - unified commit + PR history feed, Jobs-style layout.
 *
 * Layout:
 *   Page header:  breadcrumb + "Commits" title + Sync now button
 *   Shell:        one bordered rounded container
 *     Left pane:  scrollable history list + pagination
 *     Divider:    resizable vertical divider (only when an item is selected)
 *     Right pane: commit/PR detail (only when an item is selected)
 */
export function CommitsPage({
  projectUuid,
  breadcrumb,
  canEdit,
  refreshKey,
  onRunStateChange,
}: CommitsPageProps) {
  const [page, setPage] = React.useState(1);
  const [selectedUuid, setSelectedUuid] = React.useState<string | null>(null);

  const {
    state,
    loading: systemLoading,
    error: systemError,
    refresh,
    hasActiveRun,
  } = useSourceSystem(projectUuid, 'repo');
  const { state: syncState, trigger } = useManualSync(projectUuid, 'repo', 'commits');
  const { setRightPaneMode, setRightPaneVisible } = useLayout();

  const {
    items,
    total,
    loading: historyLoading,
    error: historyError,
    reload: reloadHistory,
  } = useCommitHistory(projectUuid, page, PAGE_SIZE);

  // Deselect if selected item moves off the current page
  React.useEffect(() => {
    if (selectedUuid && !items.some((i) => i.uuid === selectedUuid)) {
      setSelectedUuid(null);
    }
  }, [items, selectedUuid]);

  React.useEffect(() => {
    if (!onRunStateChange) return;
    const activeUuid = state?.activeRun?.jobRunUuid ?? null;
    const lastUuid = state?.lastRun?.jobRunUuid ?? null;
    onRunStateChange(activeUuid ?? lastUuid, activeUuid !== null);
  }, [state?.activeRun?.jobRunUuid, state?.lastRun?.jobRunUuid, onRunStateChange]);

  // Keep a stable ref to reloadHistory so the refreshKey effect doesn't
  // re-trigger every time the page changes (which would reset pagination).
  const reloadHistoryRef = React.useRef(reloadHistory);
  React.useEffect(() => {
    reloadHistoryRef.current = reloadHistory;
  });

  // Auto-refresh the history table when a sync run completes.
  const prevHasActiveRunRef = React.useRef(hasActiveRun);
  React.useEffect(() => {
    if (prevHasActiveRunRef.current && !hasActiveRun) {
      reloadHistoryRef.current();
    }
    prevHasActiveRunRef.current = hasActiveRun;
  }, [hasActiveRun]);

  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    void refresh(true);
    setPage(1);
    reloadHistoryRef.current();
  }, [refreshKey, refresh]);

  const isSyncing =
    syncState.status === 'submitting' || syncState.status === 'success' || hasActiveRun;

  async function handleTriggerSync() {
    if (!state) return;
    const firstResource = state.resources.find((r) => r.enabled) ?? state.resources[0];
    if (!firstResource) return;
    const result = await trigger(firstResource.resourceUuid);
    if (result) {
      setRightPaneMode('sync-log');
      setRightPaneVisible(true);
      void refresh(true);
    }
  }

  const totalPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 1;
  const selectedItem = selectedUuid ? (items.find((i) => i.uuid === selectedUuid) ?? null) : null;

  if (systemLoading) {
    return (
      <div className="ss-panel ss-panel--loading" aria-busy="true">
        <p>Loading Commits…</p>
      </div>
    );
  }

  if (systemError) {
    return (
      <PageErrorState message={`Failed to load: ${systemError}`} onRetry={() => void refresh()} />
    );
  }

  const hasRepos = (state?.resources.length ?? 0) > 0;

  return (
    <div className="ch-page">
      {/* Page header */}
      <header className="ss-panel__header">
        <div className="ss-panel__header-left">
          <p className="ss-panel__breadcrumb">{breadcrumb}</p>
          <h1 className="ss-panel__title">Commits</h1>
        </div>
        <div className="ss-panel__header-actions">
          {canEdit && (
            <Tooltip.Simple label="Trigger manual sync">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => void handleTriggerSync()}
                disabled={isSyncing || !state?.resources.length}
                aria-label="Trigger manual sync"
              >
                {isSyncing && <RefreshIcon spinning={true} />}
                {isSyncing ? 'Syncing…' : 'Sync now'}
              </Button>
            </Tooltip.Simple>
          )}
        </div>
      </header>

      {syncState.status === 'error' && (
        <div className="ss-panel__banner ss-panel__banner--error" role="alert">
          Trigger failed: {syncState.message}
        </div>
      )}

      <SplitPaneLayout
        loading={historyLoading}
        hasError={!!historyError}
        page={page}
        totalPages={totalPages}
        rowCount={items.length}
        onPageChange={setPage}
        selectedId={selectedUuid}
        detailTitle={
          selectedItem ? <span className="ch-page__detail-title">{selectedItem.title}</span> : null
        }
        detailContent={selectedItem ? <CommitDetail item={selectedItem} /> : null}
        onCloseDetail={() => setSelectedUuid(null)}
        dividerAriaLabel="Resize commit detail pane"
      >
        {historyLoading && (
          <p className="ch-page__loading" aria-busy="true">
            Loading history…
          </p>
        )}
        {!historyLoading && historyError && (
          <PageErrorState
            message={`Failed to load history: ${historyError}`}
            onRetry={reloadHistory}
          />
        )}
        {!historyLoading && !historyError && items.length === 0 && (
          <HistoryEmptyState hasRepos={hasRepos} />
        )}
        {!historyLoading && !historyError && items.length > 0 && (
          <div className="ch-table" role="table">
            <HistoryTableHeader />
            <div className="ch-table__body" role="rowgroup">
              {items.map((item) => (
                <HistoryItem
                  key={item.uuid}
                  item={item}
                  selected={item.uuid === selectedUuid}
                  onSelect={setSelectedUuid}
                />
              ))}
            </div>
          </div>
        )}
      </SplitPaneLayout>
    </div>
  );
}
